import React from "react";
import { useRenderTemplateWithCollections } from "../MacroBindingContext";
import { MacroHtmlEditorField } from "../fields/MacroHtmlEditorField";

export interface MacroHtmlBlockProps {
  content: string;
  className?: string;
}

const MacroHtmlBlock = {
  label: "HTML",
  llmDescription: `A raw HTML block for full control over formatting and structure. PREFERRED for complex content.
- 'content' (string): Raw HTML content - all HTML tags supported
- 'className' (string, optional): Additional CSS classes for the wrapper
- Supports {{variable}} placeholders that get substituted with binding values
- Inside repeaters, also supports collection bindings like {{program.Name}}, {{room.RoomType}}, {{pos.Description}}

USE THIS BLOCK (not Markdown) when you need:
- Underlined text: <u>underlined</u>
- Line breaks: <br> or <br />
- Links with attributes: <a href="..." target="_blank" rel="noopener">
- Bold: <strong> or <b>
- Italic: <em> or <i>
- Combined formatting: <strong><u>bold and underlined</u></strong>
- Any HTML tags or inline styles

This is the RECOMMENDED block for email content that needs:
- Mixed formatting (bold + underline, italic + link, etc.)
- Precise control over line breaks
- Links that open in new tabs

Example content:
\`\`\`
<p>Hi <strong>{{FirstName}}</strong>,</p>
<p>Thank you for your inquiry! We're <u>excited</u> to help you.</p>
<p>After all, <strong><u>This is what we do, and we do it well!</u></strong></p>
<p><em>"Our Goal is to Help You Achieve Yours, {{FirstName}}!"</em></p>
\`\`\``,
  fields: {
    content: {
      type: "custom" as const,
      label: "HTML Content",
      render: ({ name, field, value, onChange }: any) => (
        <MacroHtmlEditorField
          name={name}
          field={field}
          value={value as string | undefined}
          onChange={onChange}
        />
      ),
    },
    className: {
      type: "text" as const,
      label: "CSS Class (optional)",
    },
  },
  defaultProps: {
    content: `<p>Hi <strong>{{FirstName}}</strong>,</p>
<p>Thank you for your inquiry!</p>
<p>We're <u>excited</u> to help you with your <strong>{{ProgramDaysText}}</strong> program.</p>`,
    className: "",
  },
  render: (props: MacroHtmlBlockProps) => {
    const { content, className } = props;
    const render = useRenderTemplateWithCollections();

    // Substitute variables in the HTML content
    const substitutedContent = render(content || "");

    // Base classes for nice typography
    const baseClass =
      "macro-html-content prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0";
    const extraClass = typeof className === "string" ? className.trim() : "";
    const mergedClass = [baseClass, extraClass].filter(Boolean).join(" ");

    return (
      <div
        className={mergedClass}
        dangerouslySetInnerHTML={{ __html: substitutedContent }}
      />
    );
  },
};

export default MacroHtmlBlock;
